import { ChangeDetectorRef, Directive, inject } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { firstValueFrom, timeout } from "rxjs";
import swal from "sweetalert2";
import { ProyectosService } from "src/app/proyectos/proyectos.service";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { UserSession } from "src/app/shared/model/core/UserSession.model";
import { Empleado } from "src/app/shared/model/empleado/empleado.model";
import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";
import { DetIncBotones } from "src/app/shared/model/incidencias/detIncBotones.model";
import { EvidenciaBean } from "src/app/shared/model/incidencias/evidenciaBean.model";
import { Incidencias } from "src/app/shared/model/incidencias/incidencia.model";
import { incidenciaDetalleQABean } from "src/app/shared/model/incidencias/incidenciaDetalleQABean.model";
import { Proyectos } from "src/app/shared/model/incidencias/proyectos.model";
import { Usuario } from "src/app/shared/model/security/usuario.model";
import { EvidenciaViewerService } from "src/app/shared/services/evidencia-viewer.service";
import { GenericoService } from "src/app/shared/services/generico.service";
import { Constantes } from "src/app/utilerias/constantes/constantes";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";
import { MensajesFijosEnum } from "src/app/utilerias/enums/mensajes-fijos-enum";
import {
  parsearResolucionEstructurada,
  ResolucionEstructurada,
} from "../detallesIncidenciaDesarrollo/resolucion-causa-origen.util";
import {
  buscaEtapa,
  buscaVersion,
  colorDePrioridad,
  equipoDeEtapa,
  mismoId,
} from "../etapa-incidencia.util";
import { HistorialComponent } from "../historialAsignaciones/historial.component";
import { IncidenciasService } from "../incidencias.service";
import {
  ACCION_REVISION,
  esCentinela,
  esperaVersion,
  estadoLeido,
  MOTIVO_REVISION_PENDIENTE,
  NOMBRE_PENDIENTE,
  proximaVersionQA,
  rechazoDelGuardado,
  ResolucionEstado,
  textoEntregada,
} from "../resolucion-version.util";

/** Lo que el tablero le pasa al detalle (en history.state) o lo que se arma desde un enlace directo. */
export interface ContextoDetalle {
  incidencia: Incidencias;
  proyecto?: Proyectos | null;
}

/** Estatus en los que la resolución sigue vigente: ahí se pinta la versión en que se resolvió. */
const ESTATUS_CON_RESOLUCION: ReadonlySet<string> = new Set<string>([
  Constantes.RESUELTO,
  Constantes.REVISION,
  Constantes.VOBO,
]);

/** Consulta de proyectos de gestión de proyectos para defectos: los del usuario, activos y con versiones. */
const CONSULTA_PROYECTOS_DEFECTOS = 6;

/**
 * Lo más que se espera el estado de la resolución (ms). Después la ficha
 * sigue como si no se hubiera leído (selector), y «Marcar resuelto», que lo
 * espera deshabilitado, vuelve a estar disponible.
 */
const ESPERA_MAXIMA_ESTADO_MS = 15000;

/** El título del botón «Marcar resuelto» de Desarrollo. */
const ACCION_RESOLVER = "Resuelto";

/**
 * Lo que comparten el detalle de incidencia de QA y el de Desarrollo: las dos
 * pantallas pintan la misma plantilla (la de detallesIncidenciaDesarrollo) con
 * la columna de comentarios y su caja para escribir, las evidencias y el
 * historial de asignaciones. Cada pantalla pone sus botones, su regreso, cómo
 * repinta y qué hace después de guardar un comentario.
 */
@Directive()
export abstract class DetalleIncidenciaBase {
  protected readonly incidenciasService = inject(IncidenciasService);
  protected readonly dialog = inject(MatDialog);
  protected readonly generico = inject(GenericoService);
  protected readonly visorEvidencias = inject(EvidenciaViewerService);
  protected readonly cdr = inject(ChangeDetectorRef);
  private readonly ruta = inject(ActivatedRoute);
  private readonly proyectosService = inject(ProyectosService);

  /** Proyecto de gestión de proyectos (Mongo) de la incidencia: de él salen el equipo y las versiones de la etapa. */
  proyecto: Proyectos | null = null;
  /** Todo el equipo de la etapa, de cualquier puesto: de aquí salen los nombres del historial de asignaciones. */
  equipoEtapa: Empleado[] = [];
  /** Nombre de la versión en que se reportó (incidencia.version); nunca la de un movimiento posterior. */
  versionAfectada = "N/A";
  /** Nombre de la versión en que se resolvió, mientras la resolución siga vigente; null si no aplica. */
  versionResolucion: string | null = null;
  /**
   * INCIDENCIA_RESOLUCION_ESTADO (T3b, docs/DEFECTOS.md §6): si se resuelve
   * sin elegir versión, si la resolución espera versión y en cuál se entregó.
   * null mientras llega o si no se pudo leer: entonces nada cambia.
   */
  estadoResolucion: ResolucionEstado | null = null;
  /** La resolución espera versión: la ficha pinta «Pendiente de versión» y QA no la envía a revisión. */
  resolucionPendiente = false;
  /** «la próxima versión QA de Migración» (D3). */
  proximaVersion = "";
  /** «Se entregará en la próxima versión QA de Migración». */
  textoSeEntregara = "";
  /** «Entregada en V4-…» en una incidencia trazable ya entregada; null si no aplica. */
  entregadaEn: string | null = null;
  readonly nombrePendiente = NOMBRE_PENDIENTE;
  readonly motivoRevisionPendiente = MOTIVO_REVISION_PENDIENTE;
  /** El estado que está en camino (o el último), para quien lo necesita antes de abrir un diálogo. */
  private consultaDeEstado: Promise<ResolucionEstado | null> | null = null;
  /** Solo pinta la respuesta más reciente: una lectura vieja que llega tarde no pisa a la nueva. */
  private turnoDeEstado = 0;
  /**
   * «Marcar resuelto» (Desarrollo) espera el estado de la resolución antes de
   * abrir su diálogo: mientras llega, el botón queda deshabilitado y un
   * segundo clic no abre otro diálogo. En QA nunca se prende.
   */
  abriendoResolucion = false;
  detallemodal: Incidencias = new Incidencias();
  listadetalles: incidenciaDetalleQABean[] = [];
  listaHistorial: incidenciaDetalleQABean[] = [];
  botones: DetIncBotones[] = [];
  listaEvidenciasGeneral: EvidenciaBean[] = [];
  formComentario: FormGroup;
  listaEmpleados: Empleado[] = [];
  totalHistorial: number;
  nombreCompletoAsignado: string;
  nombreCreadoPor: string;
  nombreUsuarioLogeado: string;
  nombreProyecto: string;
  readonly commentActionTitle = "Agregar comentario";
  evidenciaComentario: File[] = [];
  submittingInlineComment = false;
  step: number;
  private readonly causasOrigenExpandidas = new Set<number>();

  /** Desarrollo separa la resolución y su causa / origen; QA muestra la descripción tal cual. */
  protected abstract readonly muestraCausaOrigen: boolean;
  /** El comentario de la caja de Desarrollo lleva también al desarrollador asignado. */
  protected abstract readonly comentarioConDesarrollador: boolean;

  /** QA repinta en el momento; Desarrollo deja la vista marcada para el siguiente ciclo. */
  protected abstract refreshView(): void;
  /** Versión que viaja con el comentario de la caja. */
  protected abstract resolveVersionId(): any;
  /** Los adjuntos del comentario no subieron (el comentario ya quedó guardado). */
  protected abstract avisaFalloDeCarga(error: unknown): void;
  /** Lo que hace cada pantalla al cerrar el aviso de comentario guardado. */
  protected abstract actualizaTrasComentario(incidencia: Incidencias): void;

  cargaNombreUsuarioLogeado() {
    try {
      const sessionRaw = localStorage.getItem("CosData");
      if (!sessionRaw) {
        this.nombreUsuarioLogeado = "";
        return;
      }

      const session = JSON.parse(sessionRaw) as UserSession;
      const persona = session?.persona;
      const nombreCompleto =
        persona?.nombreCompleto ||
        [persona?.nombre, persona?.apellidoPaterno, persona?.apellidoMaterno]
          .filter(Boolean)
          .join(" ")
          .trim();

      this.nombreUsuarioLogeado = nombreCompleto || "";
    } catch {
      this.nombreUsuarioLogeado = "";
    }
  }

  consultaNombreEmpleado(incidencia: Incidencias) {
    const usuario: Usuario = new Usuario();
    usuario.id = incidencia.id_usuario_qa;
    this.incidenciasService.consultaUsuario(usuario).subscribe((user) => {
      this.nombreCreadoPor = user.nombrePersona;
      this.refreshView();
    });
  }

  consultaNombreAsignado(incidencia: Incidencias) {
    const usuario: Usuario = new Usuario();
    usuario.id = incidencia.id_usuario_desarrollo;

    if (usuario.id != 0) {
      this.incidenciasService.consultaUsuario(usuario).subscribe((user) => {
        this.nombreCompletoAsignado = user.nombrePersona;
        this.refreshView();
      });
    } else {
      this.nombreCompletoAsignado = "N/A";
      this.refreshView();
    }
  }

  verHistorial() {
    this.dialog.open(HistorialComponent, {
      width: "auto",
      maxWidth: "96vw",
      maxHeight: "92vh",
      panelClass: "custom-modalbox",
      data: {
        dataListaHis: this.listaHistorial,
        // El historial junta reasignaciones de desarrollo y de QA: los nombres
        // salen de todo el equipo de la etapa, no sólo de los del puesto.
        dataListaEmp: [...this.equipoEtapa, ...(this.listaEmpleados ?? [])],
      },
    });
  }

  setStep(index: number) {
    this.step = index;
  }

  nextStep() {
    this.step++;
  }

  prevStep() {
    this.step--;
  }

  /**
   * Reparte el detalle que manda el backend: los comentarios (sin reasignación)
   * y el historial de asignaciones.
   */
  protected separaDetalles(respuesta: ConsultaList<incidenciaDetalleQABean>) {
    this.listadetalles = [];
    this.listaHistorial = [];

    // Si el backend falla responde 200 sin cuerpo (null).
    const lista = respuesta?.list ?? [];
    if (!lista.length) {
      return;
    }

    for (const detalle of lista) {
      if (detalle.id_emp_reasignado === 0) {
        this.listadetalles.push(detalle);
      } else {
        this.listaHistorial.push(detalle);
      }
    }
  }

  /** El proyecto de la incidencia y el equipo de su etapa; va después de asignar detallemodal. */
  protected asignaProyecto(proyecto: Proyectos | null | undefined): void {
    this.proyecto = proyecto ?? null;
    this.equipoEtapa = equipoDeEtapa(this.proyecto, this.detallemodal?.id_etapa);
  }

  /**
   * Nombres de las versiones de la ficha. La afectada es la reportada en el
   * alta (incidencia.version, la misma que el backend compara al resolver):
   * antes salía del último movimiento y, ya resuelta, se pintaba el id (UUID)
   * de la versión de resolución. Los nombres salen de la etapa del proyecto.
   */
  protected actualizaVersiones(): void {
    const idEtapa = this.detallemodal?.id_etapa;
    const idReportada = this.detallemodal?.version;
    this.versionAfectada = idReportada
      ? this.nombreDeVersion(idEtapa, idReportada, (nombre) => {
          this.versionAfectada = nombre;
        })
      : "N/A";

    // listadetalles llega del más reciente al más antiguo.
    const resolucion = ESTATUS_CON_RESOLUCION.has(this.detallemodal?.estatus)
      ? this.listadetalles.find((detalle) => !!detalle?.id_version_resuelto)
      : null;
    this.actualizaResolucionPendiente(resolucion?.id_version_resuelto);
    // El centinela no es una versión: la ficha pinta el chip «Pendiente de versión».
    this.versionResolucion =
      resolucion && !this.resolucionPendiente && !esCentinela(resolucion.id_version_resuelto)
        ? this.nombreDeVersion(idEtapa, resolucion.id_version_resuelto, (nombre) => {
            this.versionResolucion = nombre;
          })
        : null;
    this.entregadaEn = this.resolucionPendiente
      ? null
      : textoEntregada(this.estadoResolucion, this.versionResolucion);
  }

  /**
   * Si la resolución espera versión. Con el estado del servidor decide él;
   * si no se pudo leer, la última resolución del historial (el centinela).
   */
  private actualizaResolucionPendiente(idUltimaResolucion: unknown): void {
    const estado = estadoLeido(this.estadoResolucion);
    this.resolucionPendiente = estado
      ? esperaVersion(estado)
      : this.detallemodal?.estatus === Constantes.RESUELTO && esCentinela(idUltimaResolucion);
    const nombreEtapa =
      estado?.etapa?.nombre ?? buscaEtapa(this.proyecto, this.detallemodal?.id_etapa)?.nombre;
    this.proximaVersion = proximaVersionQA(nombreEtapa);
    this.textoSeEntregara = `Se entregará en ${this.proximaVersion}`;
  }

  /**
   * Vuelve a leer INCIDENCIA_RESOLUCION_ESTADO y repinta la ficha. Va al
   * cargar y después de cada movimiento; no falla: sin estado (o si no llega
   * en ESPERA_MAXIMA_ESTADO_MS), la ficha se ve como antes (selector, sin chip).
   */
  protected consultaEstadoResolucion(): void {
    const id = Number(this.detallemodal?.id);
    const turno = ++this.turnoDeEstado;
    const consulta =
      Number.isInteger(id) && id > 0
        ? firstValueFrom(
            this.incidenciasService.estadoResolucion(id).pipe(timeout(ESPERA_MAXIMA_ESTADO_MS)),
          ).catch(() => null)
        : Promise.resolve(null);
    this.consultaDeEstado = consulta.then((estado) => {
      const leido = estadoLeido(estado);
      if (turno === this.turnoDeEstado) {
        this.estadoResolucion = leido;
        this.actualizaVersiones();
        this.refreshView();
      }
      return leido;
    });
  }

  /** El estado que ya se leyó o el que está en camino (lo pide si nunca se pidió). */
  protected esperaEstadoResolucion(): Promise<ResolucionEstado | null> {
    if (this.consultaDeEstado === null) {
      this.consultaEstadoResolucion();
    }
    return this.consultaDeEstado ?? Promise.resolve(null);
  }

  /** La acción espera el estado de la resolución para abrir su diálogo («Marcar resuelto»). */
  accionEnCurso(titulo: string): boolean {
    return this.abriendoResolucion && titulo === ACCION_RESOLVER;
  }

  /** «En Revision» mientras la resolución espera versión: el servidor la rechazaría (VAL-RES-PENDIENTE). */
  accionBloqueada(titulo: string): boolean {
    return this.resolucionPendiente && titulo === ACCION_REVISION;
  }

  /** Si hay que decir por qué «Enviar a revisión» está deshabilitado. */
  revisionBloqueada(): boolean {
    return (
      this.resolucionPendiente &&
      this.botones.some((boton) => String(boton.titulo) === ACCION_REVISION)
    );
  }

  /** La fila del historial cuya resolución espera versión: su versión se pinta como chip. */
  esVersionPendiente(detalle: incidenciaDetalleQABean): boolean {
    return esCentinela(detalle?.id_version_resuelto);
  }

  /** Un movimiento que el servidor no guardó: se dice por qué y no se pinta como éxito. */
  protected avisaRechazo(texto: string): void {
    swal.fire({
      text: texto,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-danger",
      },
      icon: "error",
    });
  }

  /**
   * Nombre de la versión según la etapa del proyecto. Un id numérico que no
   * está en el proyecto es de las versiones viejas (MySQL): ese sí lo resuelve
   * consultaVersion.do, y su nombre llega después por alResolver.
   */
  private nombreDeVersion(
    idEtapa: unknown,
    idVersion: unknown,
    alResolver: (nombre: string) => void
  ): string {
    const nombre = buscaVersion(this.proyecto, idEtapa, idVersion)?.nombre;
    if (nombre) {
      return nombre;
    }

    if (/^\d+$/.test(String(idVersion))) {
      this.incidenciasService.searchVersion(String(idVersion)).subscribe({
        next: (version) => {
          if (version?.nombre) {
            alResolver(version.nombre);
            this.refreshView();
          }
        },
        error: () => {},
      });
    }
    return "Versión no disponible";
  }

  /**
   * Enlace directo (pestaña nueva, marcador o recarga sin el estado que pasa
   * el tablero): la incidencia se pide por el :id de la ruta y su proyecto se
   * busca entre los del usuario con la misma consulta del tablero. Si falta
   * cualquiera de los dos se avisa y se regresa al tablero.
   */
  protected async cargaPorEnlace(
    alCargar: (contexto: ContextoDetalle) => void,
    regresar: () => void
  ): Promise<void> {
    const contexto = await this.consultaContextoPorEnlace().catch(() => null);
    if (contexto) {
      alCargar(contexto);
      return;
    }

    await swal.fire({
      text: "No se encontró la incidencia o no tiene acceso a su proyecto.",
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-success",
      },
      icon: "info",
    });
    regresar();
  }

  private async consultaContextoPorEnlace(): Promise<ContextoDetalle | null> {
    const id = Number(this.ruta.snapshot.paramMap.get("id"));
    if (!Number.isInteger(id) || id <= 0) {
      return null;
    }

    const incidencia = await firstValueFrom(
      this.incidenciasService.consultaIncidenciaXId(id)
    );
    if (!incidencia?.id) {
      return null;
    }

    const filtro = new ConsultaList<Proyecto>();
    filtro.param6 = CONSULTA_PROYECTOS_DEFECTOS;
    filtro.regXPagina = Constantes.MIL;
    const respuesta = await firstValueFrom(
      this.proyectosService.consultaProyectoMongo(filtro)
    );
    const proyectos: Proyectos[] = Array.isArray(respuesta?.content)
      ? respuesta.content
      : [];
    const proyecto = proyectos.find((candidato) =>
      mismoId(candidato?.id, incidencia.id_proyecto)
    );
    if (!proyecto) {
      return null;
    }

    incidencia.color = colorDePrioridad(incidencia.prioridad) ?? incidencia.color;
    return { incidencia, proyecto };
  }

  protected eliminaNotificacion(idNotificacion: string) {
    const noti = {
      id: idNotificacion,
    };
    firstValueFrom(
      this.incidenciasService.obtenerElementoPorCampoPost(
        UrlConstantes.ELIMINA_NOTIFICACION_USUARIO,
        noti
      )
    )
      .then(() => {
        this.detallemodal.esComentario = Constantes.CERO;
        this.refreshView();
      })
      .catch(() => {});
  }

  number(valor: number): boolean {
    return valor > Constantes.CERO;
  }

  esPrevisualizable(evidencia: EvidenciaBean): boolean {
    return this.visorEvidencias.esPrevisualizable(evidencia?.nombre);
  }

  iconoEvidencia(evidencia: EvidenciaBean): string {
    return this.visorEvidencias.obtenIcono(evidencia?.nombre);
  }

  consultaImagenEvidencia(evidencia: EvidenciaBean) {
    evidencia.idProyecto = this.detallemodal.id_proyecto;
    evidencia.idIncidencia = this.detallemodal.id;

    if (this.visorEvidencias.esImagen(evidencia.nombre)) {
      this.muestraImagen(evidencia);
      return;
    }

    if (this.visorEvidencias.esVideo(evidencia.nombre)) {
      this.muestraVideo(evidencia);
      return;
    }

    // Excel, PDF, Word, comprimidos, etc. no se pueden previsualizar:
    // se entregan directamente al usuario como descarga.
    this.descargaEvidencia(evidencia);
  }

  muestraImagen(evidencia: EvidenciaBean) {
    this.incidenciasService.condultaImagenEvidencia(evidencia).subscribe({
      next: (res) => this.visorEvidencias.abreImagen(evidencia.nombre, res),
      error: () => this.visorEvidencias.avisoNoDisponible(),
    });
  }

  muestraVideo(evidencia: EvidenciaBean) {
    firstValueFrom(
      this.generico.consultaVideoEvidencia(
        UrlConstantes.consultaVideoEvidencia,
        evidencia
      )
    )
      .then((res) => this.visorEvidencias.abreVideo(evidencia.nombre, res))
      .catch(() => this.visorEvidencias.avisoNoDisponible());
  }

  descargaEvidencia(value: any) {
    firstValueFrom(
      this.generico.descargaEvidencia(
        UrlConstantes.descargaEvidencia,
        "INCIDENCIA",
        this.detallemodal.id_proyecto,
        this.detallemodal.id,
        value.nombre
      )
    )
      .then((res) => this.visorEvidencias.descarga(value.nombre, res))
      .catch(() => this.visorEvidencias.avisoNoDisponible());
  }

  getStatusSlug(): string {
    return (this.detallemodal?.estatus || "abierto")
      .toLowerCase()
      .replace(/\s+/g, "-");
  }

  getPrioritySlug(): string {
    return (this.detallemodal?.prioridad || "media")
      .toLowerCase()
      .replace(/\s+/g, "-");
  }

  getTypeSlug(): string {
    return (this.detallemodal?.tipo || "general")
      .toLowerCase()
      .replace(/\s+/g, "-");
  }

  getHeaderActions(): DetIncBotones[] {
    return this.botones.filter(
      (boton) => String(boton.titulo) !== this.commentActionTitle
    );
  }

  canShowCommentComposer(): boolean {
    return this.botones.some(
      (boton) => String(boton.titulo) === this.commentActionTitle
    );
  }

  canSendInlineComment(): boolean {
    return (
      !this.submittingInlineComment &&
      !!this.formComentario.get("descripcion")?.value?.trim()
    );
  }

  /**
   * Se evalúa en cada ciclo desde [disabled]: con la lista en null tronaba y
   * cortaba el ciclo entero, así que el diálogo de cambio de estatus no se
   * inicializaba y el Líder no podía mover la incidencia.
   */
  canAssignDeveloper(): boolean {
    return (this.listaEmpleados?.length ?? 0) > 0;
  }

  getInitials(nombre: string): string {
    if (!nombre) {
      return "NA";
    }

    return nombre
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((fragmento) => fragmento.charAt(0).toUpperCase())
      .join("");
  }

  getAllEvidencias(): EvidenciaBean[] {
    const seen = new Set<string>();
    const evidencias: EvidenciaBean[] = [];

    this.listadetalles.forEach((detalle) => {
      this.getDetalleEvidencias(detalle).forEach((evidencia) => {
        const key = `${evidencia.id ?? ""}-${evidencia.nombre ?? ""}`;
        if (!seen.has(key)) {
          seen.add(key);
          evidencias.push(evidencia);
        }
      });
    });

    return evidencias;
  }

  /**
   * La vista consume la lista ya calculada: llamar getAllEvidencias() desde el
   * template regeneraba el arreglo en cada ciclo de deteccion de cambios.
   */
  protected actualizaEvidenciasGenerales() {
    this.listaEvidenciasGeneral = this.getAllEvidencias();
  }

  trackByEvidencia(_index: number, evidencia: EvidenciaBean): string {
    return `${evidencia?.id ?? ""}-${evidencia?.nombre ?? ""}`;
  }

  getDetalleEvidencias(detalle: incidenciaDetalleQABean): EvidenciaBean[] {
    const raw =
      (detalle as any).listaEvidencias ??
      (detalle as any).listaEvidenica ??
      [];
    return Array.isArray(raw) ? raw : [];
  }

  getResolucionEstructurada(detalle: incidenciaDetalleQABean): ResolucionEstructurada | null {
    return this.muestraCausaOrigen
      ? parsearResolucionEstructurada(detalle?.descripcion)
      : null;
  }

  causaOrigenEstaExpandida(idDetalle: number): boolean {
    return this.causasOrigenExpandidas.has(idDetalle);
  }

  alternarCausaOrigen(idDetalle: number): void {
    if (this.causasOrigenExpandidas.has(idDetalle)) {
      this.causasOrigenExpandidas.delete(idDetalle);
    } else {
      this.causasOrigenExpandidas.add(idDetalle);
    }
  }

  getDetalleAutor(detalle: incidenciaDetalleQABean): string {
    return (
      detalle?.usuarioBean?.persona?.nombreCompleto ||
      (detalle?.usuarioBean as any)?.nombrePersona ||
      (detalle?.usuarioBean as any)?.nombrecompleto ||
      "Sistema"
    );
  }

  /** Si el comentario trae texto: uno que sólo trae adjuntos no pinta la burbuja vacía. */
  tieneTexto(detalle: incidenciaDetalleQABean): boolean {
    return String(detalle?.descripcion ?? "").trim().length > 0;
  }

  getDetalleVersion(detalle: incidenciaDetalleQABean): string | null {
    const detectada = detalle?.versionDetectadoBean;
    const resuelta = detalle?.versionResueltoBean;

    if (detectada?.isAddVersion && detectada?.nombre) {
      return detectada.nombre;
    }

    if (resuelta?.isAddVersion && resuelta?.nombre) {
      return resuelta.nombre;
    }

    return null;
  }

  addCommentFiles(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    Array.from(input.files).forEach((file) => {
      const exists = this.evidenciaComentario.some(
        (current) => current.name === file.name && current.size === file.size
      );

      if (!exists) {
        this.evidenciaComentario.push(file);
      }
    });

    input.value = "";
  }

  removeCommentFile(fileToRemove: File) {
    this.evidenciaComentario = this.evidenciaComentario.filter(
      (file) =>
        !(
          file.name === fileToRemove.name && file.size === fileToRemove.size
        )
    );
  }

  saveInlineComment() {
    if (this.submittingInlineComment) {
      return;
    }

    const descripcion = this.formComentario.get("descripcion")?.value?.trim();

    if (!descripcion) {
      swal.fire({
        text: MensajesFijosEnum.INCIDENCIA_DATOS_VACIOS,
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-danger",
        },
        icon: "error",
      });
      return;
    }

    this.submittingInlineComment = true;

    this.incidenciasService
      .guardaComentario(this.armaComentarioInline(descripcion))
      .subscribe({
        next: (incidencia) => {
          const rechazo = rechazoDelGuardado(incidencia);
          if (rechazo) {
            this.submittingInlineComment = false;
            this.avisaRechazo(rechazo);
            return;
          }
          this.subeEvidenciasComentario(incidencia);
        },
        error: () => {
          this.submittingInlineComment = false;
        },
      });
  }

  finalizaComentarioInline(incidencia: Incidencias) {
    this.submittingInlineComment = false;
    swal
      .fire({
        text: MensajesFijosEnum.INCIDENCIA_EXITOSA,
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-success",
        },
        icon: "success",
      })
      .then(() => this.actualizaTrasComentario(incidencia));
  }

  private armaComentarioInline(descripcion: string): Incidencias {
    const bean = new Incidencias();
    bean.descincidencia = descripcion;
    bean.id_version_incidencia = this.resolveVersionId();
    bean.estatus = this.detallemodal.estatus;
    bean.id = this.detallemodal.id;
    bean.id_proyecto = this.detallemodal.id_proyecto;
    bean.fecha_creacion = this.detallemodal.fecha_creacion;
    bean.id_incidencia_proyecto = this.detallemodal.id_incidencia_proyecto;
    if (this.comentarioConDesarrollador) {
      bean.id_usuario_desarrollo = this.detallemodal.id_usuario_desarrollo;
    }
    bean.id_usuario_qa = this.detallemodal.id_usuario_qa;
    bean.esComentario = Constantes.UNO;
    return bean;
  }

  /** Sube los adjuntos del comentario recién guardado y después avisa; sin adjuntos avisa de una vez. */
  private subeEvidenciasComentario(incidencia: Incidencias) {
    if (this.evidenciaComentario.length === 0) {
      this.finalizaComentarioInline(incidencia);
      return;
    }

    const formData = new FormData();
    for (const archivo of this.evidenciaComentario) {
      formData.append(
        "file",
        archivo,
        `INCIDENCIA$QA$${incidencia.id}$${incidencia.detalleBean.id}$${incidencia.id_proyecto}$${archivo.name}`
      );
    }

    this.incidenciasService.uploadEvidencia(formData).subscribe({
      error: (error) => {
        this.avisaFalloDeCarga(error);
        this.finalizaComentarioInline(incidencia);
      },
      complete: () => {
        this.finalizaComentarioInline(incidencia);
      },
    });
  }
}
